import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { Nav, Tab } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SmsIcon from "../../assets/icons/originator.svg2";
import TelephoneIcon from "../../assets/icons/icon_mobile_number-01.svg2";
import TokenEditor from "../Builder/Components/TokenEditor";
import Input from "../Builder/nodes/Logic/Inputs/Input.tsx";
import SMSCounter from "../Builder/Components/SMSCounter";
import { loadContactOptionsPaginated, proxyAPI } from "../proxyAPI.ts";
import { ScheduleSettingsForm } from "../Schedulers/ScheduleSettings.tsx";
import { SaveAsTemplateForm } from "./SaveAsTemplate";
import MyModal from "../Common/MyModal.tsx";

const handlerActionOptions = [
  { value: "forward:email", label: "Forward to email" },
  { value: "forward:sms", label: "Forward to SMS" },
  { value: "run-project", label: "Connect to a project" },
  { value: "auto-reply", label: "Auto-reply message" },
  { value: "no-action", label: "No action" },
];

function getHandlerActionLabel(h) {
  let act = h.Do;
  if (act === "forward") act = `${act}:${h.ForwardMethod}`;
  return handlerActionOptions.find((a) => a.value === act)?.label;
}

export default function BroadcastSendModals({
  action,
  isTest,
  values,
  recipientTotal,
  send,
  sendList,
  broadcastUUID,
  onHide,
}) {
  const navigate = useNavigate();
  const [testContact, setTestContact] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const handlersWithProjects = values.Handlers?.filter(
      (p) => p.Do === "run-project" && p.ProjectId
    );
    if (!handlersWithProjects?.length) {
      setProjects([]);
      return;
    }
    (async () => {
      const sp = await proxyAPI({
        prefix: "logic/workflows/api",
        url: `/project-handlers-list?${handlersWithProjects
          .map((h) => `ids=${h.ProjectId}`)
          .join("&")}`,
      });
      if (sp?.length) {
        setProjects(sp);
      }
    })();
  }, [values.Handlers]);

  return (
    <>
      {/* Send / Send Test modal */}
      <Modal
        show={action === "send-broadcast" || action === "send-test"}
        onHide={onHide}
        size="lg"
        contentClassName="bg-purple-gradient p-4"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="fw-bold">
              We&apos;re ready to send your message broadcast
            </div>
          </Modal.Title>
        </Modal.Header>
        <hr className="text-secondary mx-3 mt-0" />

        <Modal.Body>
          <Tab.Container id="BroadcastSendSummaryTabs" defaultActiveKey="sms">
            <Nav
              variant="tabs"
              className="fw-bold bg-colored-reverse justify-content-between rounded-top-2 p-2 pb-0 "
            >
              <div className="text-white m-2 p-1">
                The following messages will be sent to{" "}
                <span className="badge bg-secondary">{recipientTotal}</span>{" "}
                contacts :
              </div>
              <div className="d-flex">
                {values.Messages.map((message) => (
                  <Nav.Item key={message.Channel}>
                    <Nav.Link eventKey={message.Channel}>
                      <SmsIcon width={25} className="m-1" />
                      {message.Channel.toUpperCase()}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </div>
            </Nav>
            <Tab.Content className="bg-white rounded-bottom-3">
              {values.Messages.map((message) => (
                <Tab.Pane eventKey={message.Channel} key={message.Channel}>
                  <div className="p-3">
                    <div className="bg-purple-gradient-reverse d-flex rounded-3 p-3 ">
                      <div className="bg-icon bg-accross rounded-3 p-3">
                        <TelephoneIcon width={25} />
                      </div>
                    </div>
                    <hr className="text-secondary" />
                    {message.Channel === "email" && (
                      <>
                        <div className="row mb-3">
                          <div className="fw-bold col-3">Email subject:</div>
                          <div className="col-9">
                            <div className="bg-purple-gradient rounded-3 p-3">
                              <TokenEditor
                                preview="sms"
                                rawEditorState={
                                  message.EmailSubject.EditorState
                                }
                                onChange={() => null}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="fw-bold col-3">Email from:</div>
                          <div className="col-9">
                            <div className="bg-purple-gradient rounded-3 p-3">
                              <TokenEditor
                                preview="sms"
                                rawEditorState={message.EmailFrom.EditorState}
                                onChange={() => null}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="row mb-3">
                      <div className="fw-bold col-3">Message Preview:</div>
                      <div className="col-9">
                        <div className="bg-purple-gradient rounded-3 p-3">
                          <TokenEditor
                            preview="sms"
                            rawEditorState={message.Message.EditorState}
                            onChange={() => null}
                          />
                        </div>
                        {message.Channel === "sms" && (
                          <>
                            <SMSCounter
                              text={
                                values.Messages.find(
                                  (m) => m.Channel === "sms"
                                )?.Message.PlainText?.trim() ?? ""
                              }
                            />
                            {(values.Messages.find((m) => m.Channel === "sms")
                              ?.Tokens?.length ?? 0) > 0 && (
                              <div className="text-danger">
                                SMS character count will not be accurate when
                                you use tokens.
                              </div>
                            )}
                            {(values.Messages.find((m) => m.Channel === "sms")
                              ?.Shortlinks?.startingWith?.length ?? 0) > 0 && (
                              <div className="text-danger">
                                SMS character count will not be accurate when
                                you use shortlinks.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Tab.Pane>
              ))}
            </Tab.Content>
          </Tab.Container>

          <div className="p-3 bg-white rounded-3 my-3">
            <div className="row">
              <div className="fw-bold col-3">Message direction:</div>
              <div className="col-9">
                {values.CommsMethod === "two-way" ? "Two-way" : "One-way"}
              </div>
            </div>
          </div>

          <div className="p-3 bg-white rounded-3 my-3">
            <div className="row">
              <div className="fw-bold col-3">Reply handlers:</div>
              <div className="col-9">
                {values.Handlers?.length > 0
                  ? values.Handlers?.filter((h) => !h.IsDeliveryStatus).map(
                      (h) => (
                        <div
                          key={`${h.Do}-${h.ForwardMethod || ""}-${
                            h.ForwardTo || ""
                          }-${(h.Keywords || []).join("|")}-${
                            h.Message?.PlainText || ""
                          }`}
                          className="row gy-2 mb-3 pb-3 border-bottom"
                        >
                          <div className="col-3 fw-bold">Match keywords </div>
                          {h.Keywords?.length > 0 ? (
                            <div className="col-9">
                              {h.Keywords.map((k) => (
                                <span className="badge bg-colored" key={k}>
                                  {k}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="col-9">Handle all replies</div>
                          )}
                          <div className="col-3 fw-bold">Handler method</div>
                          <div className="col-9">
                            {getHandlerActionLabel(h)}
                          </div>
                          {h.Do === "forward" && (
                            <>
                              <div className="col-3 fw-bold">Forward to </div>
                              <div className="col-9">{h.ForwardTo}</div>
                            </>
                          )}
                          <div className="col-3 fw-bold">Message</div>
                          <div className="col-9">
                            <div className="bg-purple-gradient rounded-3 p-3">
                              <TokenEditor
                                preview="sms"
                                rawEditorState={h.Message.EditorState}
                                onChange={() => null}
                              />
                            </div>
                          </div>
                        </div>
                      )
                    )
                  : "Ignored"}{" "}
              </div>
            </div>
          </div>

          <div className="p-3 bg-white rounded-3 my-3">
            <div className="row">
              <div className="fw-bold col-3">Status handlers:</div>
              <div className="col-9">
                {values.Handlers?.length > 0
                  ? values.Handlers?.filter((h) => h.IsDeliveryStatus)?.map(
                      (h) => (
                        <div
                          key={`${h.Do}-${h.ProjectId || ""}-${
                            h.ForwardTo || ""
                          }-${(h.Keywords || []).join("|")}-${
                            h.Message?.PlainText || ""
                          }`}
                          className="row gy-2 mb-3 pb-3 border-bottom"
                        >
                          <div className="col-3 fw-bold">Match Statuses </div>
                          {h.Keywords?.length > 0 ? (
                            <div className="col-9">
                              {h.Keywords.map((k) => (
                                <span className="badge bg-colored" key={k}>
                                  {k}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div className="col-9">Handle all replies</div>
                          )}
                          <div className="col-3 fw-bold">Handler method</div>
                          <div className="col-9">
                            {getHandlerActionLabel(h)}
                          </div>
                          {h.Do === "forward" && (
                            <>
                              <div className="col-3 fw-bold">Forward to </div>
                              <div className="col-9">{h.ForwardTo}</div>
                            </>
                          )}
                          {h.Do !== "run-project" ? (
                            <>
                              <div className="col-3 fw-bold">Message</div>
                              <div className="col-9">
                                <div className="bg-purple-gradient rounded-3 p-3">
                                  <TokenEditor
                                    preview="sms"
                                    rawEditorState={h.Message.EditorState}
                                    onChange={() => null}
                                  />
                                </div>
                              </div>
                            </>
                          ) : (
                            (() => {
                              const project = projects.find(
                                (p) => p.ID === h.ProjectId
                              );
                              return (
                                <>
                                  <div className="col-3 fw-bold">
                                    Project label
                                  </div>
                                  <div className="col-9">{project?.Label}</div>
                                  <div className="col-3 fw-bold">
                                    Project reference
                                  </div>
                                  <div className="col-9">
                                    {project?.Reference}
                                  </div>
                                  <div className="col-3 fw-bold">
                                    Project description:
                                  </div>
                                  <div className="col-9">
                                    {project?.Description}
                                  </div>
                                </>
                              );
                            })()
                          )}
                        </div>
                      )
                    )
                  : "Ignored"}{" "}
              </div>
            </div>
          </div>

          {isTest && (
            <div className="my-2 p-3 rounded-3 bg-white">
              Send test message to:
              <Input
                value={[testContact]}
                onChange={([v]) => setTestContact(v)}
                loadOptions={loadContactOptionsPaginated}
              />
            </div>
          )}

          <div className="p-4">
            By sending this broadcast, I confirm that my organisation has
            recorded that the recipients included in the broadcast have given
            their prior consent to receive communications, and have also been
            provided with the option to opt-out of any further communications.
          </div>
          <hr className="text-secondary mt-4 mb-1" />
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="bg-dark px-5 text-white border-0 rounded-pill"
            onClick={onHide}
          >
            CANCEL
          </Button>
          <Button
            className="bg-colored-reverse px-5 text-white border-0 rounded-pill"
            onClick={async () => {
              const res = await send(isTest ? testContact : null);
              if (res) {
                onHide();
              }
            }}
          >
            SEND
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Schedule modal */}
      <MyModal
        show={action === "schedule-broadcast"}
        heading="Save your template"
        size="lg"
        onHide={onHide}
      >
        {broadcastUUID === "new" ? (
          <>
            <div className="alert alert-secondary">
              You need to save your broadcast as a template before you can
              schedule it.
            </div>
            <SaveAsTemplateForm
              key={values.Uuid}
              sendList={sendList}
              isDefaultSaveContacts
              onSave={(data) => {
                navigate(
                  `/library/broadcast/${data.Uuid}/messages?action=schedule-broadcast`
                );
              }}
            />
          </>
        ) : (
          <ScheduleSettingsForm
            broadcasterUuid={broadcastUUID}
            showNewScheduleByDefault
            handleClose={onHide}
          />
        )}
      </MyModal>
    </>
  );
}
